/**
 * Hand a freshly registered driver over to the driver portal (the public charging SPA
 * served at /charge/). Registration already signed the browser in with the HttpOnly
 * session cookie, and both SPAs share the origin, so the portal recognises the same
 * session (it asks /api/auth/me). Nothing is copied into Web Storage or the URL.
 */
export const DRIVER_PORTAL_ACCOUNT_PATH = "/charge/account";

const DRIVER_PORTAL_PREFIX = "/charge/";
const MAX_RETURN_PATH_LENGTH = 2048;

/**
 * The portal destination after registration: `next` when it is a same-origin path inside
 * the portal (`/charge/...`, e.g. the charger page the driver came from), otherwise the
 * portal account page. Refused: absolute and protocol-relative URLs, backslashes, `.`/`..`
 * segments, whitespace/control characters and malformed escapes — checked on the raw value
 * and once more after one decode, so an encoded `//`, `\` or `..` is refused too.
 */
export function safeDriverPortalReturn(next: string | null | undefined): string {
  if (typeof next !== "string" || next.length > MAX_RETURN_PATH_LENGTH) return DRIVER_PORTAL_ACCOUNT_PATH;
  if (!next.startsWith(DRIVER_PORTAL_PREFIX)) return DRIVER_PORTAL_ACCOUNT_PATH;
  let decoded: string;
  try {
    decoded = decodeURIComponent(next);
  } catch {
    return DRIVER_PORTAL_ACCOUNT_PATH;
  }
  for (const value of [next, decoded]) {
    if (!value.startsWith(DRIVER_PORTAL_PREFIX)) return DRIVER_PORTAL_ACCOUNT_PATH;
    if (/[\\\s\u0000-\u001f\u007f]/.test(value) || value.includes("//")) return DRIVER_PORTAL_ACCOUNT_PATH;
    const path = value.split(/[?#]/, 1)[0];
    if (path.split("/").some((seg) => seg === "." || seg === "..")) return DRIVER_PORTAL_ACCOUNT_PATH;
  }
  return next;
}

export function handOffToDriverPortal(next?: string | null) {
  window.location.assign(safeDriverPortalReturn(next));
}

export const DRIVER_PORTAL_LOGIN_PATH = "/charge/account/login";

/**
 * The driver portal's sign-in page, returning to the same safe destination as the hand-off.
 * The portal's `next` is relative to its /charge basename (`/charge/A1` → `next=/A1`); an
 * unsafe `next` becomes the portal account page. A different SPA, so link with a plain href.
 */
export function driverPortalLoginHref(next?: string | null): string {
  const inPortal = safeDriverPortalReturn(next).slice(DRIVER_PORTAL_PREFIX.length - 1);
  return `${DRIVER_PORTAL_LOGIN_PATH}?${new URLSearchParams({ next: inPortal })}`;
}
